import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPScrollablePane, IPSearchBox, IPText } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import BaseTableList from 'components/TableList/BaseTableList';
import { globalFilterDelay } from 'globalConstants';
import {
  globalSearchBoxStyles,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { AuthSchema } from 'models/auth/authSchema';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageKeys } from 'utils/localstorage';
import { sortOnBoolean, sortOnString } from 'utils/sorting';
import { getRandomId } from 'utils/string';

interface IAuthSchemaListProps {
  authSchemas: AuthSchema[];
  isLoading?: boolean;
  showSearch?: boolean;
  onSelectAuthSchema: (item: AuthSchema | undefined) => void;
  onChooseAuthSchema: (item: AuthSchema | undefined) => void;
}

const AuthSchemaList = (props: IAuthSchemaListProps) => {
  const { t } = useTranslation(['translation', 'adminAuth']);
  const appContext = useContext(AppContext);
  const [authSchemasDisplay, setAuthSchemasDisplay] = useState<AuthSchema[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [selectionResetKey] = useState<string>(getRandomId());

  //
  // Effects
  //
  useEffect(() => {
    setAuthSchemasDisplay(filterActive(props.authSchemas));

    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setAuthSchemasDisplay(filterActive(props.authSchemas));
    } else {
      const queryLower = searchQuery.toLowerCase();
      const filtered = props.authSchemas.filter((entry) => {
        return Object.values(entry).some((val) => {
          if (typeof val === 'string') {
            if (entry.name && entry.name.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
              return true;
            }
          }

          return false;
        });
      });

      if (filtered) {
        setAuthSchemasDisplay(filterActive(filtered));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.authSchemas, searchQuery]);

  //
  // Columns list view
  //
  const getColumns = (): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'name',
        name: t('adminAuth:AuthSchemaPicker.Columns.Name'),
        minWidth: 150,
        maxWidth: 300,
        isRowHeader: true,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'description',
        name: t('adminAuth:AuthSchemaPicker.Columns.Description'),
        minWidth: 150,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
        isCollapsible: true,
      },
    ];

    if (appContext.user.isAdmin()) {
      allColumns.push({
        key: 'active',
        name: t('adminAuth:AuthSchemaPicker.Columns.Active'),
        minWidth: 80,
        maxWidth: 80,
        isMultiline: true,
        isCollapsible: true,
      });
    }

    return allColumns;
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumns());

  const onRenderCol = (item: AuthSchema, column: IColumn): JSX.Element | null => {
    if (!item) return null;

    switch (column.key) {
      case 'name':
        return <IPText>{item.name}</IPText>;
      case 'description':
        return <IPText>{item.description}</IPText>;
      case 'active':
        return <IPCheckbox checked={item.active}></IPCheckbox>;
      default:
        return null;
    }
  };

  const onCompare = (a: AuthSchema, b: AuthSchema, columnKey: string): number => {
    switch (columnKey) {
      case 'name':
        return sortOnString(a.name, b.name);
      case 'description':
        return sortOnString(a.description, b.description);
      case 'active':
        return sortOnBoolean(a.active, b.active);
      default:
        return 0;
    }
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  const filterActive = (schemas: AuthSchema[]): AuthSchema[] => {
    if (appContext.user.isAdmin()) {
      return schemas;
    } else {
      return schemas.filter((a) => a.active);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {props.showSearch && (
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPSearchBox
                autoFocus={!appContext.isMobileView}
                underlined
                placeholder={t('translation:General.Filter.Placeholder')}
                id="search"
                onChange={(ev, newValue) => {
                  if (searchTimer) {
                    clearTimeout(searchTimer);
                  }
                  setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
                }}
                styles={globalSearchBoxStyles}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <BaseTableList
            compact
            isLoading={props.isLoading}
            items={authSchemasDisplay}
            columns={columnsList}
            selectionMode={IPSelectionMode.single}
            onRenderCol={onRenderCol}
            compare={onCompare}
            setColumns={setColumnsList}
            cacheKeySort={LocalStorageKeys.AuthSchemaListSort}
            onSelect={(items) => {
              props.onSelectAuthSchema(items.length > 0 ? items[0] : undefined);
            }}
            selectionResetKey={selectionResetKey}
            onRowDbClick={(item) => props.onChooseAuthSchema(item)}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default AuthSchemaList;
